const API = "https://photospicker.googleapis.com/v1";

export type PickerSession = {
  id: string;
  pickerUri: string;
  mediaItemsSet?: boolean;
};

export type PickedMediaItem = {
  id: string;
  // RFC 3339 timestamp of when the photo was taken (per Photos Picker API).
  // Can be missing on rare edge cases (camera without timestamp metadata).
  createTime?: string;
  mediaFile: {
    baseUrl: string;
    mimeType: string;
  };
};

async function call<T>(token: string, path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Photos Picker API ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

export async function createPickerSession(token: string): Promise<PickerSession> {
  return call<PickerSession>(token, "/sessions", { method: "POST", body: "{}" });
}

export async function pollPickerSession(token: string, sessionId: string): Promise<PickerSession> {
  return call<PickerSession>(token, `/sessions/${sessionId}`);
}

export async function listPickedMedia(token: string, sessionId: string): Promise<PickedMediaItem[]> {
  const result = await call<{ mediaItems?: PickedMediaItem[] }>(
    token,
    `/mediaItems?sessionId=${encodeURIComponent(sessionId)}&pageSize=100`
  );
  return result.mediaItems ?? [];
}

// `popup` must be a window reference opened SYNCHRONOUSLY by the caller
// inside the click handler — `window.open('about:blank', '_blank')` —
// before any await. Mobile Safari blocks popups created after async work.
// Once the picker session URL is ready, we navigate the already-open
// popup to it.
export async function pickPhotos(
  token: string,
  popup: Window | null
): Promise<PickedMediaItem[]> {
  const session = await createPickerSession(token);
  if (popup && !popup.closed) {
    popup.location.href = session.pickerUri;
  } else {
    // Popup was blocked. Fall back to navigating this tab — user loses
    // app state but at least the picker opens.
    window.location.href = session.pickerUri;
    throw new Error("Photo picker opened in this tab — please allow popups and try again");
  }
  // Poll every 2 seconds for up to 5 minutes
  for (let i = 0; i < 150; i++) {
    await new Promise((r) => setTimeout(r, 2000));
    const status = await pollPickerSession(token, session.id);
    if (status.mediaItemsSet) {
      return listPickedMedia(token, session.id);
    }
  }
  throw new Error("Photos Picker session timed out (5 minutes)");
}
